import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { DomTools } from "@cervval.js/client-html5-core/tools";
import { CjsInput, InputOptions } from "../../components/inputs/input";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

export type InputTextEditorOptions = EditorOptions & {

    /** unescape HTML entities from editor value (for example '&gt;' will be displayed '>') */
    unescapeHTML?: boolean
};

export const defaultInputTextEditorOptions: InputTextEditorOptions = {
    ...defaultEditorOptions,

    /** Default behavior is to unescape html entities */
    unescapeHTML: true
};

/**
 * Class representing a component with an input text
 */
@CustomElement({
    name: "cjs-input-text-editor",
    style: require("./inputTextEditor.scss"),
    template: require("./inputTextEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsInputTextEditor extends CjsEditor<string> {

    /** Attributes options of InputTextEditor */
    @TypedWebComponentOptions(defaultInputTextEditorOptions)
    public readonly options!: WebComponentOptions<InputTextEditorOptions>;

    /** Attributes options of input of InputTextEditor */
    public get inputOptions(): WebComponentOptions<InputOptions<string>> {
        return this.getInput().options;
    }

    /** Components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInput] })
    public readonly components!: {
        inputText: CjsInput<string>
    };

    constructor(options?: Partial<InputTextEditorOptions>, inputOptions?: Partial<InputOptions<string>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.inputOptions.setWithoutRefresh(inputOptions);
    }

    /** Get input of Editor, where user set data */
    public getInput(): CjsInput<string> {
        return this.components.inputText;
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        // Callback for input components
        this.getInput().changeEvent.on((event: Event) => this._onInputValueChange(event));
        this.getInput().addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._onInputValueChange(e);
            }
        });

    }

    /** Refresh component display */
    public override refresh(): void {

        // Refresh
        super.refresh();

        // Set options
        this.inputOptions.setWithoutRefresh({ value: this._getValueToBeDisplayed() });

        // Always refresh input
        this.getInput().refresh();
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().input;
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        this.inputOptions.setAndRefreshIfChanged({ value: this._getValueToBeDisplayed() });
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): string | undefined {
        return this.getInput().value;
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.inputOptions.setWithoutRefresh({ disabled: this.disabled });
    }

    /**
     * Get value to displayed on the text input
     * @returns 
     */
    private _getValueToBeDisplayed(): string | undefined {
        return this.options.unescapeHTML && this._value ? DomTools.htmlStringToText(this._value) : this._value;
    }
}